<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { TimepickerUI } from 'timepicker-ui'
import 'timepicker-ui/main.css'

const input = ref<HTMLInputElement | null>(null)
let picker: TimepickerUI | null = null

const props = defineProps<{
  modelValue?: string | null
}>()

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

function formatConfirmValue(data: { hour?: string; minutes?: string; type?: string }) {
  return `${data.hour}:${data.minutes} ${data.type}`
}

function normalizeTimeForPicker(value: string) {
  const match = value.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i)
  if (!match) return value
  return `${match[1]}:${match[2]} ${match[3].toUpperCase()}`
}

function getCurrentTimeFormatted() {
  const now = new Date()
  let hours = now.getHours()
  const minutes = now.getMinutes()
  const type = hours >= 12 ? 'PM' : 'AM'
  hours = hours % 12
  hours = hours === 0 ? 12 : hours
  return `${hours}:${minutes.toString().padStart(2, '0')} ${type}`
}

function getInitialTime() {
  if (props.modelValue) {
    return normalizeTimeForPicker(props.modelValue)
  }
  return getCurrentTimeFormatted()
}

function applyTimeToPicker(time: string) {
  if (!input.value) return
  input.value.value = time
  picker?.setValue(time)
}

onMounted(() => {
  if (!input.value) return

  const initialTime = getInitialTime()
  const hadModelValue = !!props.modelValue

  input.value.value = initialTime

  picker = new TimepickerUI(input.value, {
    clock: {
      type: '12h',
      incrementMinutes: 5,
    },
    ui: {
      mode: 'clock',
      // mobile: true keeps the dial collapsed until hour/minute is tapped.
      // false shows the analog clock immediately (Material-style picker).
      mobile: false,
      editable: false,
      animation: true,
      theme: 'basic',
    },
    callbacks: {
      onConfirm: (data) => {
        emit('update:modelValue', formatConfirmValue(data))
      },
    },
  })

  picker.create()
  setTimeout(() => {
    applyTimeToPicker(initialTime)
    if (!hadModelValue) {
      emit('update:modelValue', initialTime)
    }
  }, 800)
})

watch(
  () => props.modelValue,
  (newValue) => {
    if (!newValue) return
    applyTimeToPicker(normalizeTimeForPicker(newValue))
  }
)

onUnmounted(() => {
  picker?.destroy()
  picker = null
})
</script>

<template>
  <input
    ref="input"
    :value="modelValue ?? ''"
    type="text"
    inputmode="none"
    readonly
    class="w-full rounded-xl border border-gray-300 pl-10 px-4 py-3 text-sm focus:border-violet-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
    placeholder="Select time"
  />
</template>
